import { useEffect, useState } from 'react';
import ReplayIcon from '@mui/icons-material/Replay';
import { IconButton, InputAdornment, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import StyledTextField from 'components/styled/StyledTextField';

const AttendanceSummaryTable = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'month'));
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const columns = [
    {
      field: 'userName',
      headerName: 'User',
      sortable: false,
      filterable: false,
      minWidth: 170,
      renderCell: (params) => {
        const name = params.row?.userName;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'month',
      headerName: 'Month',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        const month = params.row?.month;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {month}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'year',
      headerName: 'Year',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        const year = params.row?.year;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {year}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'presentDays',
      headerName: 'Present Days',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.presentDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'halfDays',
      headerName: 'Half Days',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.halfDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'absentDays',
      headerName: 'Absent Days',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.absentDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'paidLeaveDays',
      headerName: 'Paid Leaves',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.paidLeaveDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'unpaidLeaveDays',
      headerName: 'UnPaid Leaves',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.unpaidLeaveDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'payableDays',
      headerName: 'Payable Days',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.payableDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'totalWorkingDays',
      headerName: 'Working Days',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.totalWorkingDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'totalCalendarDays',
      headerName: 'Calender Days',
      sortable: false,
      filterable: false,
      minWidth: 70,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.totalCalendarDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'rerun',
      headerName: 'Re Run',
      sortable: false,
      filterable: false,
      width: 80,
      renderCell: (params) => {
        return (
          <IconButton
            onClick={() => handleRerunAttendance(params.row)}
            loading={loading}
            size="small"
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  const handleRerunAttendance = async (data) => {
    setLoading(true);
    try {
      const month = selectedMonth.format('M');
      const year = selectedMonth.format('YYYY');
      const res = await post('admin/rerun_monthly_summary', {
        userId: data.userId,
        month: month,
        year: year,
      });
      if (res.status == 200) {
        fetchData();
        enqueueSnackbar(res.message || 'Success', { variant: 'success' });
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const month = selectedMonth.format('M');
      const year = selectedMonth.format('YYYY');
      const res = await post('/users/MonthlyAttendanceSummary', {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search,
        month,
        year,
      });
      if (res.status == 200) {
        setRows(res.data.data);
        setTotal(res.data.total);
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error' });
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, paginationModel, search]);
  return (
    <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <StyledTextField
            id="search-box"
            type="search"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search User Name"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              maxWidth: { sm: 200, md: 240 },
              flexGrow: { xs: 1, sm: 0 },
              mb: 3,
            }}
          />

          <DatePicker
            views={['month', 'year']}
            label="Month"
            value={selectedMonth}
            maxDate={moment().subtract(1, 'month')}
            onChange={(newValue) => {
              setSelectedMonth(newValue);
              console.log(newValue.format('MM-YYYY'));
            }}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 160 },
              },
            }}
          />
        </Box>
      </LocalizationProvider>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={false}
          autoHeight
          paginationModel={paginationModel} // { page: 0, pageSize: 10 }
          onPaginationModelChange={(e) => {
            setPaginationModel(() => ({
              page: e.page,
              pageSize: e.pageSize,
              search: search,
            }));
          }}
          rowCount={total}
          pageSizeOptions={[5, 10, 20]}
        />
      </Box>
    </Paper>
  );
};

export default AttendanceSummaryTable;
